// ai-model.js — the ONE place the app's `$ai` model id is decided.
//
// WHY THIS EXISTS (owner decision D1, 2026-08-08): the ward-agent model bake-off
// (120 samples/model — see OPD_REBUILD_LOG.md) picked Cerebras `gpt-oss-120b`
// over the long-standing OpenAI `gpt-5.4-mini` default, and the swap must be a
// CONFIG change, not a code change. Before this module the model id was
// hardcoded in nine agent files; a provider/model switch meant editing all nine
// and re-shipping. Now every `$ai` call resolves its model here (runAgentLoop in
// medplum-ai.js calls this when the caller passes no explicit model).
//
// HOW A DEPLOYMENT SWITCHES MODEL — two halves, flipped TOGETHER:
//   1. `aiModel` in the deployment config (`medplum-demo.json`, the same baked
//      file that carries baseUrl/projectId) — the model id string this module
//      returns, e.g. "gpt-oss-120b".
//   2. The Medplum PROJECT secrets `LLM_BASE_URL` + `OPENAI_API_KEY` — `$ai`
//      (Medplum-native POST fhir/R4/$ai, run as the logged-in user) reads the
//      project's secrets, not our config, to pick the provider endpoint.
//   The model id MUST be one the configured provider actually serves: config
//   says gpt-oss-120b while the project secret still points at OpenAI (or vice
//   versa) → every `$ai` call fails loud with an unknown-model error.
//
// Absent/empty `aiModel` → DEFAULT_AI_MODEL (the single baked default — local
// sandboxes and older deploy configs keep working unchanged). A PRESENT but
// non-string `aiModel` is a malformed deployment config and throws (same
// fail-loud stance as the missing-projectId login check, AGENTS.md §2).
//
// Scope: the run-as-user `$ai` surfaces only (consult command agent, Spaces,
// global assistant, ward/round/assessment/template/care-plan/ED extractors).
// Studio's server-side Cerebras pipeline and the server-side consult LLM keep
// their own server-env model configuration — different processes, different
// keys, deliberately not this module's business.
import { loadRawConfig } from './client-port.js';

export const DEFAULT_AI_MODEL = 'gpt-5.4-mini';

/** The model id every `$ai` agent call should use, per deployment config. */
export async function aiModel() {
  const cfg = await loadRawConfig();
  const raw = cfg?.aiModel;
  if (raw === undefined || raw === null || raw === '') return DEFAULT_AI_MODEL;
  if (typeof raw !== 'string') {
    throw new Error(`deployment config "aiModel" must be a string model id, got ${typeof raw}`);
  }
  return raw.trim() || DEFAULT_AI_MODEL;
}
